import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { ja } from 'date-fns/locale'
import i18next from 'i18next'

function getDateLocale() {
  return i18next.language === 'ja' ? { locale: ja } : {}
}

// ============================================================================
// Class Name Utilities
// ============================================================================

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ============================================================================
// Date Formatting
// ============================================================================

export function formatDate(dateString: string | null | undefined, formatStr = 'PPP'): string {
  if (!dateString) return 'N/A'
  try {
    return format(parseISO(dateString), formatStr, getDateLocale())
  } catch {
    return dateString
  }
}

export function formatDateTime(dateString: string | null | undefined): string {
  return formatDate(dateString, 'PPP p')
}

export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A'
  try {
    return formatDistanceToNow(parseISO(dateString), { addSuffix: true, ...getDateLocale() })
  } catch {
    return dateString
  }
}

/**
 * #061 — Compact absolute datetime ("May 20, 7:24 AM") for pills/badges where
 * the exact time matters (e.g. settings applied/pending). Locale-aware via
 * toLocaleString. Returns '—' for null/invalid input.
 */
export function formatAbsoluteDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '—'
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

/**
 * CP 272 — exact date and time for the event cards ("6 Oct, 14:32"), in the
 * viewer's locale and time zone like formatAbsoluteDateTime, plus the year
 * when it is not the current year ("23 Jul 2025, 09:39"). `locale`,
 * `timeZone` and `now` exist for tests. Returns '—' for null/invalid input.
 */
export function formatCardDateTime(
  dateString: string | null | undefined,
  opts: { now?: Date; locale?: string; timeZone?: string } = {},
): string {
  if (!dateString) return '—'
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return '—'
  const { now = new Date(), locale, timeZone } = opts
  const yearOf = (x: Date) => x.toLocaleString('en-US', { year: 'numeric', timeZone })
  return d.toLocaleString(locale, {
    month: 'short', day: 'numeric',
    ...(yearOf(d) !== yearOf(now) && { year: 'numeric' }),
    hour: 'numeric', minute: '2-digit',
    timeZone,
  })
}

// ============================================================================
// File Size Formatting
// ============================================================================

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)

  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

/**
 * Format SD card usage as "used/total" with consistent units.
 * Handles MB inputs; promotes to GB once total >= 1024 MB. Returns just total
 * if used is null. Returns null if total is null/0.
 */
export function formatSDCard(usedMb: number | null | undefined, totalMb: number | null | undefined): string | null {
  if (totalMb == null || totalMb <= 0) return null
  const promote = totalMb >= 1024
  const fmt = (mb: number | null | undefined) => {
    if (mb == null) return '?'
    return promote ? `${(mb / 1024).toFixed(1)}` : `${Math.round(mb)}`
  }
  const unit = promote ? 'GB' : 'MB'
  if (usedMb == null) return `${fmt(totalMb)} ${unit}`
  return `${fmt(usedMb)}/${fmt(totalMb)} ${unit}`
}

// ============================================================================
// Duration Formatting
// ============================================================================

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '--:--'

  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// ============================================================================
// Avatar Utilities
// ============================================================================

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
]

/** Deterministic avatar background color from a username string. */
export function avatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

// ============================================================================
// String Utilities
// ============================================================================

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function formatEventType(eventType: string): string {
  return eventType
    .split('_')
    .map(capitalize)
    .join(' ')
}

// ============================================================================
// Object Utilities
// ============================================================================

export function removeEmptyValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    )
  ) as Partial<T>
}

// ============================================================================
// Debounce
// ============================================================================

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// ============================================================================
// Detection Type Helpers
// ============================================================================

export const detectionTypeColors: Record<string, string> = {
  person_detected: 'text-blue-500',
  dog_detected: 'text-amber-500',
  car_detected: 'text-purple-500',
  motion_detected: 'text-green-500',
  sound_detected: 'text-red-500',
}

export const detectionTypeIcons: Record<string, string> = {
  person_detected: 'User',
  dog_detected: 'Dog',
  car_detected: 'Car',
  motion_detected: 'Activity',
  sound_detected: 'Volume2',
}
